//***************************************************
// WebLinkEditor.tsx
// Web/YouTubeリンクの遠隔設定（選択中デバイス向け）
//***************************************************
import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Button, message, Modal, List, Card, Spin, Form, Input, Empty } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, MenuOutlined } from "@ant-design/icons";
import { collection, query, where, getDocs, doc, writeBatch } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { Device, Link } from "./types";
import axios from "axios";

interface LinkFormData {
  title: string;
  url: string;
  description: string;
}

type LocalLink = Link & { localId: string };

const getIdOffset = (mode: number): number => {
  switch (mode) {
    case 1: return 101; case 2: return 201; case 3: return 301;
    case 4: return 401; case 5: return 501; case 6: return 601;
    default: return 701;
  }
};

/** Firestore に書き込む際は localId を含めない */
function stripLocalId(link: LocalLink): Omit<LocalLink, "localId"> {
  const { localId: _unused, ...rest } = link;
  return rest;
}

export type WebLinkEditorHandle = {
  open: (type: "web" | "youtube") => void;
};

export interface WebLinkEditorProps {
  selectedDevice: Device | null;
  /** ボタンを横並びのラッパーで包む場合用 */
  className?: string;
  style?: React.CSSProperties;
  hideTrigger?: boolean;
}

const WebLinkEditor = forwardRef<WebLinkEditorHandle, WebLinkEditorProps>(({ selectedDevice, className, style, hideTrigger }, ref) => {
  const [editingModalType, setEditingModalType] = useState<"web" | "youtube" | null>(null);
  const [webLinks, setWebLinks] = useState<LocalLink[]>([]);
  const [youtubeLinks, setYoutubeLinks] = useState<LocalLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState<LocalLink | null>(null);
  const [currentMode, setCurrentMode] = useState<1 | 4>(1);
  const [form] = Form.useForm();
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [draggedListMode, setDraggedListMode] = useState<1 | 4 | null>(null);

  const handleOpenModal = (type: "web" | "youtube") => {
    if (selectedDevice) {
      void fetchLinks(selectedDevice.serialNumber);
      setEditingModalType(type);
    } else {
      message.warning("デバイスを選択してください。");
    }
  };

  useImperativeHandle(ref, () => ({ open: handleOpenModal }), [selectedDevice]);

  const fetchLinks = async (serialNumber: string) => {
    setIsLoading(true);
    try {
      const q = query(collection(db, "linkitems"), where("serialNumber", "==", serialNumber));
      const querySnapshot = await getDocs(q);
      const allLinks: LocalLink[] = querySnapshot.docs.map((d) => ({
        ...(d.data() as Link),
        id: d.id,
        localId: d.id,
      }));
      const sortedLinks = allLinks.sort(
        (a, b) =>
          (typeof a.id === "string" ? parseInt(a.id.split("_").pop() || "0", 10) : Number(a.id)) -
          (typeof b.id === "string" ? parseInt(b.id.split("_").pop() || "0", 10) : Number(b.id))
      );
      setWebLinks(sortedLinks.filter((link) => link.mode === 1));
      setYoutubeLinks(sortedLinks.filter((link) => link.mode === 4));
    } catch (error) {
      console.error("Error fetching links: ", error);
      message.error("リンクデータの取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number, mode: 1 | 4) => {
    setDraggedItemIndex(index);
    setDraggedListMode(mode);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent, droppedOnIndex: number, mode: 1 | 4) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedListMode !== mode) return;
    const links = mode === 1 ? [...webLinks] : [...youtubeLinks];
    const [draggedItem] = links.splice(draggedItemIndex, 1);
    links.splice(droppedOnIndex, 0, draggedItem);
    if (mode === 1) setWebLinks(links);
    else setYoutubeLinks(links);
    setDraggedItemIndex(null);
    setDraggedListMode(null);
  };

  const handleAddNew = (mode: 1 | 4) => {
    setEditingLink(null);
    setCurrentMode(mode);
    form.resetFields();
    setIsFormModalVisible(true);
  };
  const handleEdit = (link: LocalLink) => {
    setEditingLink(link);
    setCurrentMode(link.mode as 1 | 4);
    form.setFieldsValue({ title: link.title, url: link.url, description: link.description });
    setIsFormModalVisible(true);
  };
  const handleDelete = (linkToDelete: LocalLink) => {
    if (linkToDelete.mode === 1) setWebLinks((p) => p.filter((l) => l.localId !== linkToDelete.localId));
    else setYoutubeLinks((p) => p.filter((l) => l.localId !== linkToDelete.localId));
    message.info(`「${linkToDelete.title}」をリストから削除しました。最後に更新ボタンを押してください。`);
  };
  const handleFormSave = (values: LinkFormData) => {
    if (!selectedDevice) return;
    if (editingLink) {
      const updatedLink = { ...editingLink, ...values };
      if (updatedLink.mode === 1)
        setWebLinks((p) => p.map((l) => (l.localId === updatedLink.localId ? updatedLink : l)));
      else setYoutubeLinks((p) => p.map((l) => (l.localId === updatedLink.localId ? updatedLink : l)));
    } else {
      const newLink: LocalLink = {
        ...values,
        mode: currentMode,
        id: "",
        serialNumber: selectedDevice.serialNumber,
        updateTime: Date.now(),
        localId: `new_${Date.now()}`,
      };
      if (currentMode === 1) setWebLinks((p) => [...p, newLink]);
      else setYoutubeLinks((p) => [...p, newLink]);
    }
    setIsFormModalVisible(false);
  };

  const handleUpdateAllAndNotify = async () => {
    if (!selectedDevice) return;
    setIsLoading(true);
    try {
      const batch = writeBatch(db);
      const serialNumber = selectedDevice.serialNumber;
      const allLinksQuery = query(collection(db, "linkitems"), where("serialNumber", "==", serialNumber));
      const querySnapshot = await getDocs(allLinksQuery);
      const unmanagedLinksByMode: { [key: number]: Link[] } = {};
      querySnapshot.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref);
        const link = docSnapshot.data() as Link;
        if (link.mode !== 1 && link.mode !== 4) {
          if (!unmanagedLinksByMode[link.mode]) unmanagedLinksByMode[link.mode] = [];
          unmanagedLinksByMode[link.mode].push(link);
        }
      });
      const processLinks = (links: LocalLink[], mode: number) => {
        const idOffset = getIdOffset(mode);
        links.forEach((link, index) => {
          const newId = idOffset + index;
          const newDocId = `${serialNumber}_${newId}`;
          const payload = stripLocalId(link);
          batch.set(doc(db, "linkitems", newDocId), {
            ...payload,
            mode,
            updateTime: Date.now(),
            id: newId,
            serialNumber,
          });
        });
      };
      processLinks(webLinks, 1);
      processLinks(youtubeLinks, 4);
      for (const modeStr in unmanagedLinksByMode) {
        const mode = parseInt(modeStr, 10);
        const sorted = unmanagedLinksByMode[mode].sort(
          (a, b) =>
            (typeof a.id === "string" ? parseInt(a.id.split("_").pop() || "0", 10) : Number(a.id)) -
            (typeof b.id === "string" ? parseInt(b.id.split("_").pop() || "0", 10) : Number(b.id))
        );
        const links: LocalLink[] = sorted.map((l, idx) => ({
          ...l,
          localId: `preserve_${mode}_${idx}`,
        }));
        processLinks(links, mode);
      }
      await batch.commit();
      message.success("リンク情報を一括更新しました。");
      if (selectedDevice.fcmToken) {
        await axios.post(
          "/api/sendFCM",
          {
            tokens: [selectedDevice.fcmToken],
            payload: {
              mode: "request",
              command: "update_links",
              user: "TANTOU",
              place: "CENTER",
              param1: String(Date.now()),
            },
          },
          { headers: { "Content-Type": "application/json" } }
        );
        message.success("デバイスに更新を通知しました。");
      } else {
        message.warning("デバイスにFCMトークンが設定されていないため、通知は行われませんでした。");
      }
      setEditingModalType(null);
    } catch (error) {
      console.error("一括更新エラー: ", error);
      message.error("タブレットデータの更新に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
      <div className={className} style={{ display: "flex", justifyContent: "flex-start", gap: "8px", flexWrap: "wrap", ...style }}>
        <Button type="primary" onClick={() => handleOpenModal("web")} disabled={!selectedDevice}>
          Webリンクを編集
        </Button>
        <Button type="primary" onClick={() => handleOpenModal("youtube")} disabled={!selectedDevice}>
          Youtubeリンクを編集
        </Button>
      </div>
      )}

      <Modal
        title={`${editingModalType === "web" ? "Webリンク" : "YouTubeリンク"}設定 - ${selectedDevice?.deviceName || ""}`}
        open={editingModalType !== null}
        onCancel={() => setEditingModalType(null)}
        width={800}
        destroyOnClose
        footer={[
          <Button key="back" onClick={() => setEditingModalType(null)}>
            キャンセル
          </Button>,
          <Button key="submit" type="primary" loading={isLoading} onClick={() => void handleUpdateAllAndNotify()}>
            更新してタブレットに通知
          </Button>,
        ]}
      >
        ※各リンクの編集や削除を実施後、最後に更新ボタンを押すことで更新が確定します。
        <br />
        <Spin spinning={isLoading}>
          {editingModalType === "web" && (
            <Card
              title="Webリンク (お役立ち)"
              extra={
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => handleAddNew(1)}>
                  追加
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              {webLinks.length === 0 ? (
                <Empty description="リンク未登録" />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={webLinks}
                  renderItem={(item, index) => (
                    <List.Item
                      key={item.localId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index, 1)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index, 1)}
                      style={{
                        cursor: "move",
                        background: draggedItemIndex === index && draggedListMode === 1 ? "#f0f8ff" : "transparent",
                      }}
                      actions={[
                        <Button key="edit" icon={<EditOutlined />} onClick={() => handleEdit(item)} />,
                        <Button key="del" icon={<DeleteOutlined />} danger onClick={() => handleDelete(item)} />,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<MenuOutlined style={{ cursor: "grab", color: "#999" }} />}
                        title={item.title}
                        description={`URL: ${item.url}`}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          )}
          {editingModalType === "youtube" && (
            <Card
              title="YouTube (お役立ち)"
              extra={
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => handleAddNew(4)}>
                  追加
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              {youtubeLinks.length === 0 ? (
                <Empty description="リンク未登録" />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={youtubeLinks}
                  renderItem={(item, index) => (
                    <List.Item
                      key={item.localId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index, 4)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index, 4)}
                      style={{
                        cursor: "move",
                        background: draggedItemIndex === index && draggedListMode === 4 ? "#f0f8ff" : "transparent",
                      }}
                      actions={[
                        <Button key="edit" icon={<EditOutlined />} onClick={() => handleEdit(item)} />,
                        <Button key="del" icon={<DeleteOutlined />} danger onClick={() => handleDelete(item)} />,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<MenuOutlined style={{ cursor: "grab", color: "#999" }} />}
                        title={item.title}
                        description={`キーワード: ${item.url}`}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          )}
        </Spin>
      </Modal>

      <Modal
        title={editingLink ? "リンクの編集" : `新しい${currentMode === 1 ? "Webリンク" : "YouTube"}の追加`}
        open={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="キャンセル"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleFormSave}>
          <Form.Item name="title" label="タイトル" rules={[{ required: true, message: "必須項目です" }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="url"
            label={currentMode === 1 ? "URL" : "検索キーワード"}
            rules={[
              { required: true, message: "必須項目です" },
              ...(currentMode === 1 ? [{ type: "url" as const, message: "有効なURLを入力してください" }] : []),
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="説明" rules={[{ required: true, message: "必須項目です" }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
});

WebLinkEditor.displayName = "WebLinkEditor";

export default WebLinkEditor;
